import { Trash2, Bell, AlertCircle } from 'lucide-react';
import Input from './elements/Input';
import Info from './elements/Info';
import { toActualSeconds, toDateTimeLocal } from '../utils/globalUtils';

const AlertManager = ({ alert, onAlertChange, onClear }) => {
  const currentYear = new Date().getUTCFullYear();

  const handleAlertChange = (field, value) => {
    let updates = { [field]: value };

    // When changing alert type, determine which fields to keep
    if (field === 'alertType') {
      if (value === 'none') {
        updates = { ...updates, alertDay: null, alertMonth: null, alertYear: null, alertMessage: null, alertErrorField: null, alertError: null };
      } else if (value === 'monthly') {
        updates = { ...updates, alertMonth: null, alertYear: null };
      } else if (value === 'yearly') {
        updates = { ...updates, alertYear: null };
      } else if (value === 'one-time' && !alert?.alertYear) {
        updates = { ...updates, alertYear: currentYear };
      }
    }

    // Build the complete alert object
    const updatedAlert = {
      alertType: alert?.alertType || 'none',
      alertDay: alert?.alertDay || null,
      alertMonth: alert?.alertMonth || null,
      alertYear: alert?.alertYear || (alert?.alertType === 'one-time' ? currentYear : null),
      alertMessage: alert?.alertMessage || null,
      alertErrorField: alert?.alertErrorField || null,
      alertError: alert?.alertError || null,
      ...updates
    };

    if (updates.alertDay && (updates.alertDay < 1 || updates.alertDay > 31)) {
      updatedAlert.alertErrorField = 'alertDay';
      updatedAlert.alertError = 'Day must be between 1 and 31.';
    } else if (updates.alertMonth && (updates.alertMonth < 1 || updates.alertMonth > 12)) {
      updatedAlert.alertErrorField = 'alertMonth';
      updatedAlert.alertError = 'Month must be between 1 and 12.';
    } else if (updates.alertYear && (updates.alertYear < 1900 || updates.alertYear > currentYear + 100)) {
      updatedAlert.alertErrorField = 'alertYear';
      updatedAlert.alertError = `Year must be between 1900 and ${currentYear + 100}.`;
    } else if (updatedAlert.alertDay && updatedAlert.alertMonth && !validDayInMonth(updatedAlert.alertMonth, updatedAlert.alertDay)) {
      updatedAlert.alertErrorField = 'alertDate';
      updatedAlert.alertError = 'Selected month has fewer days. Please select a valid day for the month.';
    } else if (updatedAlert.alertType === 'one-time' && updatedAlert.alertDay && updatedAlert.alertMonth && updatedAlert.alertYear && new Date(Date.UTC(updatedAlert.alertYear, updatedAlert.alertMonth - 1, updatedAlert.alertDay, 0, 30, 0)) <= new Date()) {
      updatedAlert.alertErrorField = 'alertDate';
      updatedAlert.alertError = 'Selected date is in the past. Please select a future date.';
    } else if (updates.alertMessage && updates.alertMessage.length > 500) {
      updatedAlert.alertErrorField = 'alertMessage';
      updatedAlert.alertError = 'Message must not exceed 500 characters.';
    } else {
      updatedAlert.alertErrorField = null;
      updatedAlert.alertError = null;
    }

    onAlertChange(updatedAlert);
  };

  const getNextAlertDateInSeconds = () => {
    if (!alert?.alertType || alert.alertType === 'none' || !alert.alertDay) return null;
    const now = new Date();
    let nextAlertDate = null;
    if (alert.alertType === 'monthly') {
      nextAlertDate = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), alert.alertDay, 0, 30, 0));
      if (nextAlertDate < now) {
        nextAlertDate.setUTCMonth(nextAlertDate.getUTCMonth() + 1);
      }
    } else if (alert.alertType === 'yearly') {
      if (!alert.alertMonth) return null;
      nextAlertDate = new Date(Date.UTC(now.getUTCFullYear(), alert.alertMonth - 1, alert.alertDay, 0, 30, 0));
      if (nextAlertDate < now) {
        nextAlertDate.setUTCFullYear(nextAlertDate.getUTCFullYear() + 1);
      }
    } else if (alert.alertType === 'one-time') {
      if (!alert.alertMonth || !alert.alertYear) return null;
      nextAlertDate = new Date(Date.UTC(alert.alertYear, alert.alertMonth - 1, alert.alertDay, 0, 30, 0));
      if (nextAlertDate < now) {
        nextAlertDate = null; // One-time alert in the past, so no next date
      }
    }
    
    return nextAlertDate ? toActualSeconds(nextAlertDate) : null;
  };

  const validDayInMonth = (month, day) => {
    const thirtyDayMonths = [4, 6, 9, 11];
    if (thirtyDayMonths.includes(month) && day > 30) return false;
    if (month === 2) {
      // Check for leap year
      const year = alert?.alertYear || currentYear;
      const isLeapYear = (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0);
      if (day > 29) return false;
      if (!isLeapYear && day > 28) return false;
    }
    return true;
  };

  const handleClearAlert = () => {
    if (onClear) {
      onClear();
    }
  };

  return (
    <>
      <div className="flex items-center justify-between mb-2 border-b border-gray-300 pb-1">
        <div className="flex items-center gap-2">
          <Bell className="w-5 h-5 text-accent-main" />
          <h3 className="text-lg font-semibold text-primary-dark">Alert & Reminder</h3>
        </div>
        {(alert?.alertType !== 'none' || alert?.alertDay || alert?.alertMonth || alert?.alertYear || alert?.alertMessage) && (
          <button
            onClick={handleClearAlert}
            className="text-red-600 hover:text-red-800 text-sm flex items-center gap-1"
            title="Remove alert"
          >
            <Trash2 className="w-4 h-4" />
            Clear
          </button>
        )}
      </div>

      <div className="flex flex-col gap-2">
        <div className="grid sm:grid-cols-4 gap-2">
          <Input
            label="Alert Type"
            type="select"
            value={alert?.alertType || 'none'}
            onChange={(e) => handleAlertChange('alertType', e.target.value)}
            onClear={() => handleAlertChange('alertType', 'none')}
          >
            <option value="none">No Alert</option>
            <option value="one-time">One-time</option>
            <option value="yearly">Yearly</option>
            <option value="monthly">Monthly</option>
          </Input>

          {(alert?.alertType === 'monthly' || alert?.alertType === 'yearly' || alert?.alertType === 'one-time') ? (
            <Input
              type="text"
              label="Day"
              value={alert?.alertDay || ''}
              onChange={(e) => handleAlertChange('alertDay', e.target.value ? parseInt(e.target.value) : null)}
              placeholder="1-31"
              min="1"
              max="31"
              error={alert?.alertErrorField === 'alertDay' || alert?.alertErrorField === 'alertDate' ? ' ' : null}
            />
          ) : (
            <div></div>
          )}

          {(alert?.alertType === 'yearly' || alert?.alertType === 'one-time') ? (
            <Input
              label="Month"
              type="select"
              value={alert?.alertMonth || ''}
              onChange={(e) => handleAlertChange('alertMonth', e.target.value ? parseInt(e.target.value) : null)}
              error={alert?.alertErrorField === 'alertMonth' || alert?.alertErrorField === 'alertDate' ? ' ' : null}
            >
              <option value="">Select</option>
              <option value="1">January</option>
              <option value="2">February</option>
              <option value="3">March</option>
              <option value="4">April</option>
              <option value="5">May</option>
              <option value="6">June</option>
              <option value="7">July</option>
              <option value="8">August</option>
              <option value="9">September</option>
              <option value="10">October</option>
              <option value="11">November</option>
              <option value="12">December</option>
            </Input>
          ) : (
            <div></div>
          )}

          {alert?.alertType === 'one-time' ? (
            <Input
              type="number"
              label="Year"
              value={alert?.alertYear || currentYear}
              onChange={(e) => handleAlertChange('alertYear', e.target.value ? parseInt(e.target.value) : null)}
              placeholder="2025"
              min="1000"
              onClear={() => handleAlertChange('alertYear', currentYear)}
              error={alert?.alertErrorField === 'alertYear' || alert?.alertErrorField === 'alertDate' ? ' ' : null}
            />
          ) : (
            <div></div>
          )}
        </div>

        <Input
          type="textarea"
          label={`Message (${(alert?.alertMessage || '').length}/500)`}
          value={alert?.alertMessage || ''}
          onChange={(e) => handleAlertChange('alertMessage', e.target.value)}
          placeholder="Enter your reminder message..."
          rows={3}
          error={alert?.alertErrorField === 'alertMessage' ? ' ' : null}
          disabled={alert?.alertType === 'none'}
        />

        {alert?.alertError && (
          <Info type="error" icon={AlertCircle}>
            <p>{alert.alertError}</p>
          </Info>
        )}

        {!alert?.alertError && (
          <Info>
            {alert.alertType === 'monthly' && (
              alert.alertDay ? (
                <p>You’ll be reminded on <strong>day {alert.alertDay}</strong> of every month.</p>
              ) : <p>Select a <strong>day (1-31)</strong> to set a monthly reminder.</p>)}
            {alert.alertType === 'yearly' && (
              (alert.alertMonth && alert.alertDay) ? (
                <p>You’ll be reminded every year on <strong>{['', 'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'][alert.alertMonth]} {alert.alertDay}</strong>.</p>
              ) : <p>Select a <strong>month and day</strong> to set a yearly reminder.</p>)}
            {alert.alertType === 'one-time' && (
              (!alert.alertYear || !alert.alertMonth || !alert.alertDay) ? (
                <p>Select a <strong>month, day and year</strong> to set a one-time reminder.</p>
              ) : (
                <></>
              )
            )}
            {(alert.alertType === 'none') ? (
              <p>Select an <strong>alert type</strong> to enable reminders.</p>
            ) : null}
            {getNextAlertDateInSeconds() && (
              <p>
                Next reminder: <strong>{toDateTimeLocal(getNextAlertDateInSeconds(), { weekday: "long" }).split(',').splice(0, 2).join(',')}</strong>
              </p>
            )}
          </Info>
        )}
      </div>
    </>
  );
};

export default AlertManager;
